import { useCallback, useEffect, useMemo, useState } from 'react';

import AgentsStep from '../components/AgentsStep';
import CodebaseStep from '../components/CodebaseStep';
import FooterBar from '../components/FooterBar';
import GitHubProfileMenu from '../components/GitHubProfileMenu';
import SkillInfoModal from '../components/InfoModals';
import PricingStep, { ResultEmailPromptModal } from '../components/PricingStep';
import ScanSummary from '../components/ScanSummary';
import SkillsStep from '../components/SkillsStep';
import WorkflowShell from '../components/WorkflowShell';
import {
  INITIAL_STEP,
  clearGitHubCallbackParams,
  compactScopePaths,
  getAiModelKey,
  getDiscoveryAgentModels,
  getGitHubCallbackError,
  getGitHubSetupInstallationId,
  getInitialGitHubSessionId,
  getNextWorkflowStep,
  getOrderedSelectedAiModels,
  getPreviousWorkflowStep,
  getSelectedAiAgentsWithModels,
  getStoredScanDraft,
  isPricingTierAvailable,
  normalizeList,
  requestJson,
  storeGitHubInstallationId,
  storeGitHubSessionId,
  storeScanDraft,
  type AgentDiscoveryResponse,
  type AgentModelsByAgent,
  type CodebaseSourceMode,
  type GitHubAppConfigResponse,
  type GitHubBranchesResponse,
  type GitHubInstallationSummary,
  type GitHubInstallationsResponse,
  type GitHubRepositoriesByInstallation,
  type GitHubRepositoriesResponse,
  type GitHubRepositoryBranchSummary,
  type GitHubRepositoryChoice,
  type GitHubRepositoryDirectorySummary,
  type GitHubRepositoryTreeResponse,
  type GitHubSessionResponse,
  type LaunchState,
  type LoadState,
  type PricingEstimateResponse,
  type PricingTier,
  type ScanAcceptedResponse,
  type ScanWorkflowDraft,
  type StepId
} from '../components/workflowShared';

interface BlockchainAuditPageProps {
  onNavigate: () => void;
}

function BlockchainAuditPage({ onNavigate }: BlockchainAuditPageProps) {
  const initialScanDraft = useMemo(() => getStoredScanDraft(), []);
  const [currentStep, setCurrentStep] = useState<StepId>(() => initialScanDraft?.currentStep ?? INITIAL_STEP);
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [loadError, setLoadError] = useState('');
  const [availableSkills, setAvailableSkills] = useState<string[]>([]);
  const [availableAiAgents, setAvailableAiAgents] = useState<string[]>([]);
  const [agentModelsByAgent, setAgentModelsByAgent] = useState<AgentModelsByAgent>({});
  const [selectedSkills, setSelectedSkills] = useState<Set<string>>(() => new Set());
  const [selectedAiModels, setSelectedAiModels] = useState<Set<string>>(() => new Set());
  const [codebaseSourceMode, setCodebaseSourceMode] = useState<CodebaseSourceMode>(
    () => initialScanDraft?.codebaseSourceMode ?? 'github'
  );
  const [uploadedCodebaseFile, setUploadedCodebaseFile] = useState<File | null>(null);
  const [uploadedCodebaseFileError, setUploadedCodebaseFileError] = useState('');
  const [githubConfigState, setGithubConfigState] = useState<LoadState>('idle');
  const [githubConfigError, setGithubConfigError] = useState('');
  const [githubConfig, setGithubConfig] = useState<GitHubAppConfigResponse | null>(null);
  const [githubSessionId] = useState(() => getInitialGitHubSessionId());
  const [githubLogin, setGithubLogin] = useState('');
  const [githubSessionState, setGithubSessionState] = useState<LoadState>('idle');
  const [githubSessionError, setGithubSessionError] = useState(getGitHubCallbackError());
  const [githubInstallations, setGithubInstallations] = useState<GitHubInstallationSummary[]>([]);
  const [selectedGithubInstallationId, setSelectedGithubInstallationId] = useState<number | null>(
    () => getGitHubSetupInstallationId()
  );
  const [githubRepositoriesState, setGithubRepositoriesState] = useState<LoadState>('idle');
  const [githubRepositoriesError, setGithubRepositoriesError] = useState('');
  const [githubRepositoriesByInstallation, setGithubRepositoriesByInstallation] =
    useState<GitHubRepositoriesByInstallation>({});
  const [selectedGithubRepositoryId, setSelectedGithubRepositoryId] = useState<number | null>(
    () => initialScanDraft?.selectedGithubRepositoryId ?? null
  );
  const [githubBranchesState, setGithubBranchesState] = useState<LoadState>('idle');
  const [githubBranchesError, setGithubBranchesError] = useState('');
  const [githubBranches, setGithubBranches] = useState<GitHubRepositoryBranchSummary[]>([]);
  const [selectedGithubBranch, setSelectedGithubBranch] = useState(
    () => initialScanDraft?.selectedGithubBranch ?? ''
  );
  const [githubTreeState, setGithubTreeState] = useState<LoadState>('idle');
  const [githubTreeError, setGithubTreeError] = useState('');
  const [githubTreeDirectories, setGithubTreeDirectories] = useState<GitHubRepositoryDirectorySummary[]>([]);
  const [scopePaths, setScopePaths] = useState<string[]>(() => initialScanDraft?.scopePaths ?? []);
  const [nonStandardTokens, setNonStandardTokens] = useState<'yes' | 'no' | ''>(
    () => initialScanDraft?.questionnaire.nonStandardTokens ?? ''
  );
  const [trustedRoles, setTrustedRoles] = useState(() => initialScanDraft?.questionnaire.trustedRoles ?? '');
  const [regularFunctions, setRegularFunctions] = useState(
    () => initialScanDraft?.questionnaire.regularFunctions ?? ''
  );
  const [adminRestrictions, setAdminRestrictions] = useState(
    () => initialScanDraft?.questionnaire.adminRestrictions ?? ''
  );
  const [assumptionsFilePath, setAssumptionsFilePath] = useState(
    () => initialScanDraft?.questionnaire.assumptionsFilePath ?? ''
  );
  const [externalContractsTrusted, setExternalContractsTrusted] = useState<'yes' | 'no' | ''>(
    () => initialScanDraft?.questionnaire.externalContractsTrusted ?? ''
  );
  const [launchState, setLaunchState] = useState<LaunchState>('idle');
  const [launchError, setLaunchError] = useState('');
  const [scanResponse, setScanResponse] = useState<ScanAcceptedResponse | null>(null);
  const [resultEmail, setResultEmail] = useState('');
  const [isResultEmailModalOpen, setIsResultEmailModalOpen] = useState(false);
  const [selectedPricingTier, setSelectedPricingTier] = useState<PricingTier | null>('free');
  const discountCode = '';
  const [pricingEstimateState, setPricingEstimateState] = useState<LoadState>('idle');
  const [pricingEstimateError, setPricingEstimateError] = useState('');
  const [pricingEstimate, setPricingEstimate] = useState<PricingEstimateResponse | null>(null);
  const [showSkillInfoModal, setShowSkillInfoModal] = useState(false);

  const orderedSelectedSkills = useMemo(() => {
    return availableSkills.filter((skill) => selectedSkills.has(skill));
  }, [availableSkills, selectedSkills]);
  const orderedSelectedAiModels = useMemo(() => {
    return getOrderedSelectedAiModels(availableAiAgents, agentModelsByAgent, selectedAiModels);
  }, [availableAiAgents, agentModelsByAgent, selectedAiModels]);
  const scopePath = scopePaths.join(', ');
  const selectedGithubInstallation = githubInstallations.find((installation) => {
    return installation.id === selectedGithubInstallationId;
  });
  const githubRepositoryChoices = useMemo<GitHubRepositoryChoice[]>(() => {
    return githubInstallations.flatMap((installation) => {
      const repositories = githubRepositoriesByInstallation[installation.id] ?? [];

      return repositories.map((repository) => ({
        installation,
        repository
      }));
    });
  }, [githubInstallations, githubRepositoriesByInstallation]);
  const selectedGithubRepository = (selectedGithubInstallationId
    ? githubRepositoriesByInstallation[selectedGithubInstallationId] ?? []
    : []
  ).find((repository) => {
    return repository.id === selectedGithubRepositoryId;
  });
  const uploadedCodebaseFileName = uploadedCodebaseFile?.name ?? '';
  const target = codebaseSourceMode === 'upload'
    ? uploadedCodebaseFileName
    : selectedGithubRepository?.fullName ?? '';
  const selectedModelCount = orderedSelectedAiModels.length;
  const hasAcceptedScan = launchState === 'accepted' && scanResponse !== null;
  const selectedPricingTierAvailable = isPricingTierAvailable(selectedPricingTier);
  const hasGithubCodebaseTarget = Boolean(
    githubSessionId &&
    selectedGithubInstallationId &&
    selectedGithubRepository &&
    githubBranchesState === 'ready' &&
    selectedGithubBranch
  );
  const hasUploadedCodebaseTarget = Boolean(
    uploadedCodebaseFile &&
    !uploadedCodebaseFileError
  );
  const hasCodebaseTarget = codebaseSourceMode === 'upload'
    ? hasUploadedCodebaseTarget
    : hasGithubCodebaseTarget;
  const canAdvance =
    loadState === 'ready' && (
      (currentStep === 'agents' && selectedModelCount > 0) ||
      (currentStep === 'skills' && selectedSkills.size > 0) ||
      (currentStep === 'codebase' && hasCodebaseTarget) ||
      (
        currentStep === 'pricing' &&
        selectedPricingTierAvailable &&
        pricingEstimateState === 'ready' &&
        launchState !== 'submitting'
      )
    );

  const buildScanDraft = useCallback((step: StepId): ScanWorkflowDraft => ({
    selectedScanType: 'smart-contract',
    currentStep: step,
    selectedSkills: Array.from(selectedSkills),
    selectedAiModels: Array.from(selectedAiModels),
    codebaseSourceMode,
    uploadedCodebaseFileName: uploadedCodebaseFile?.name ?? '',
    selectedGithubRepositoryId,
    selectedGithubBranch,
    scopePath,
    scopePaths,
    questionnaire: {
      nonStandardTokens,
      trustedRoles,
      regularFunctions,
      adminRestrictions,
      assumptionsFilePath,
      externalContractsTrusted
    },
    selectedPricingTier,
    discountCode
  }), [
    selectedSkills,
    selectedAiModels,
    codebaseSourceMode,
    uploadedCodebaseFile,
    selectedGithubRepositoryId,
    selectedGithubBranch,
    scopePath,
    scopePaths,
    nonStandardTokens,
    trustedRoles,
    regularFunctions,
    adminRestrictions,
    assumptionsFilePath,
    externalContractsTrusted,
    selectedPricingTier,
    discountCode
  ]);

  const viewRun = useCallback((runId: string) => {
    storeScanDraft({
      ...buildScanDraft(INITIAL_STEP),
      selectedScanType: null
    });
    setCurrentStep(INITIAL_STEP);
    setLaunchState('idle');
    setScanResponse(null);
    setLaunchError('');

    const nextPath = `/run/${encodeURIComponent(runId)}`;

    window.history.pushState({}, document.title, nextPath);
    onNavigate();
  }, [buildScanDraft, onNavigate]);

  const resetScanResult = useCallback((resetPricing = false) => {
    setLaunchState('idle');
    setScanResponse(null);
    setLaunchError('');

    if (resetPricing) {
      setSelectedPricingTier('free');
    }
  }, []);

  const backToScanTypeSelection = useCallback(() => {
    storeScanDraft({
      ...buildScanDraft(INITIAL_STEP),
      selectedScanType: null
    });
    setCurrentStep(INITIAL_STEP);
    setLaunchState('idle');
    setScanResponse(null);
    setLaunchError('');

    if (window.location.pathname !== '/') {
      window.history.pushState({}, document.title, '/');
      onNavigate();
    }
  }, [buildScanDraft, onNavigate]);

  const loadOptions = useCallback(async () => {
    setLoadState('loading');
    setLoadError('');

    try {
      const discovery = await requestJson<AgentDiscoveryResponse>('/agents');
      const skills = normalizeList(discovery.skills);
      const aiAgents = normalizeList(discovery.aiAgents);
      const aiAgentModels = getDiscoveryAgentModels(discovery, aiAgents);
      const storedDraft = getStoredScanDraft();
      const validAiModelKeys = new Set(aiAgents.flatMap((agent) => {
        return (aiAgentModels[agent] ?? []).map((model) => getAiModelKey(agent, model));
      }));

      setAvailableSkills(skills);
      setAvailableAiAgents(aiAgents);
      setAgentModelsByAgent(aiAgentModels);
      setSelectedSkills(
        storedDraft
          ? new Set(storedDraft.selectedSkills.filter((skill) => skills.includes(skill)))
          : new Set(skills)
      );
      setSelectedAiModels(
        storedDraft
          ? new Set(storedDraft.selectedAiModels.filter((modelKey) => validAiModelKeys.has(modelKey)))
          : new Set()
      );
      setLoadState('ready');
    } catch (error) {
      setAvailableSkills([]);
      setAvailableAiAgents([]);
      setAgentModelsByAgent({});
      setSelectedSkills(new Set());
      setSelectedAiModels(new Set());
      setLoadError(error instanceof Error ? error.message : String(error));
      setLoadState('error');
    }
  }, []);

  useEffect(() => {
    void loadOptions();
  }, [loadOptions]);

  const loadGitHubConfig = useCallback(async () => {
    setGithubConfigState('loading');
    setGithubConfigError('');

    try {
      const config = await requestJson<GitHubAppConfigResponse>('/github/app');

      setGithubConfig(config);
      setGithubConfigState('ready');
    } catch (error) {
      setGithubConfig(null);
      setGithubConfigError(error instanceof Error ? error.message : String(error));
      setGithubConfigState('error');
    }
  }, []);

  const loadGitHubSession = useCallback(async (sessionId: string) => {
    if (!sessionId) {
      setGithubLogin('');
      setGithubInstallations([]);
      setGithubRepositoriesByInstallation({});
      setGithubSessionState('idle');
      return;
    }

    setGithubSessionState('loading');
    setGithubSessionError('');

    try {
      const [session, installationResponse] = await Promise.all([
        requestJson<GitHubSessionResponse>(`/github/session/${encodeURIComponent(sessionId)}`),
        requestJson<GitHubInstallationsResponse>(`/github/session/${encodeURIComponent(sessionId)}/installations`)
      ]);
      const installations = Array.isArray(installationResponse.installations)
        ? installationResponse.installations
        : [];

      setGithubLogin(session.login ?? '');
      setGithubInstallations(installations);
      setGithubSessionState('ready');

      setSelectedGithubInstallationId((currentInstallationId) => {
        if (currentInstallationId && installations.some((installation) => installation.id === currentInstallationId)) {
          return currentInstallationId;
        }

        return installations[0]?.id ?? null;
      });
    } catch (error) {
      setGithubLogin('');
      setGithubInstallations([]);
      setGithubRepositoriesByInstallation({});
      setGithubSessionState('error');
      setGithubSessionError(error instanceof Error ? error.message : String(error));
      storeGitHubSessionId('');
    }
  }, []);

  const loadGitHubRepositories = useCallback(async (
    sessionId: string,
    installations: GitHubInstallationSummary[]
  ) => {
    if (!sessionId || installations.length === 0) {
      setGithubRepositoriesByInstallation({});
      setGithubRepositoriesState(sessionId ? 'ready' : 'idle');
      setGithubRepositoriesError('');
      return;
    }

    setGithubRepositoriesState('loading');
    setGithubRepositoriesError('');

    try {
      const entries = await Promise.all(installations.map(async (installation) => {
        const response = await requestJson<GitHubRepositoriesResponse>(
          `/github/session/${encodeURIComponent(sessionId)}/installations/${String(installation.id)}/repositories`
        );
        const repositories = Array.isArray(response.repositories) ? response.repositories : [];

        return [installation.id, repositories] as const;
      }));
      const repositoriesByInstallation = entries.reduce<GitHubRepositoriesByInstallation>(
        (groupedRepositories, [installationId, repositories]) => ({
          ...groupedRepositories,
          [installationId]: repositories
        }),
        {}
      );

      setGithubRepositoriesByInstallation(repositoriesByInstallation);
      setGithubRepositoriesState('ready');
    } catch (error) {
      setGithubRepositoriesByInstallation({});
      setSelectedGithubRepositoryId(null);
      setGithubRepositoriesState('error');
      setGithubRepositoriesError(error instanceof Error ? error.message : String(error));
    }
  }, []);

  const loadGitHubBranches = useCallback(async (
    sessionId: string,
    installationId: number,
    repositoryId: number
  ) => {
    if (!sessionId || !installationId || !repositoryId) {
      setGithubBranches([]);
      setGithubBranchesState('idle');
      setGithubBranchesError('');
      setSelectedGithubBranch('');
      return;
    }

    setGithubBranchesState('loading');
    setGithubBranchesError('');

    try {
      const response = await requestJson<GitHubBranchesResponse>(
        `/github/session/${encodeURIComponent(sessionId)}/installations/${String(installationId)}/repositories/${String(repositoryId)}/branches`
      );
      const branches = Array.isArray(response.branches) ? response.branches : [];
      const fallbackBranch = branches.find((branch) => branch.default)?.name ?? branches[0]?.name ?? '';

      setGithubBranches(branches);
      setSelectedGithubBranch((currentBranch) => {
        return branches.some((branch) => branch.name === currentBranch)
          ? currentBranch
          : fallbackBranch;
      });
      setGithubBranchesState('ready');
    } catch (error) {
      setGithubBranches([]);
      setSelectedGithubBranch('');
      setGithubBranchesState('error');
      setGithubBranchesError(error instanceof Error ? error.message : String(error));
    }
  }, []);

  const loadGitHubRepositoryTree = useCallback(async (
    sessionId: string,
    installationId: number,
    repositoryId: number,
    branch: string,
    signal: AbortSignal
  ) => {
    if (!sessionId || !installationId || !repositoryId || !branch) {
      setGithubTreeDirectories([]);
      setGithubTreeState('idle');
      setGithubTreeError('');
      return;
    }

    setGithubTreeState('loading');
    setGithubTreeError('');

    try {
      const response = await requestJson<GitHubRepositoryTreeResponse>(
        `/github/session/${encodeURIComponent(sessionId)}/installations/${String(installationId)}/repositories/${String(repositoryId)}/tree?branch=${encodeURIComponent(branch)}`,
        { signal }
      );
      const directories = Array.isArray(response.directories) ? response.directories : [];

      setGithubTreeDirectories(directories);
      setScopePaths((currentScopePaths) => {
        const availablePaths = new Set(directories.map((directory) => directory.path));

        return compactScopePaths(currentScopePaths.filter((path) => availablePaths.has(path)));
      });
      setGithubTreeState('ready');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }

      setGithubTreeDirectories([]);
      setGithubTreeState('error');
      setGithubTreeError(error instanceof Error ? error.message : String(error));
    }
  }, []);

  useEffect(() => {
    void loadGitHubConfig();
  }, [loadGitHubConfig]);

  useEffect(() => {
    if (loadState !== 'ready') {
      return;
    }

    storeScanDraft(buildScanDraft(currentStep));
  }, [
    loadState,
    currentStep,
    buildScanDraft
  ]);

  useEffect(() => {
    if (githubSessionId) {
      storeGitHubSessionId(githubSessionId);
      void loadGitHubSession(githubSessionId);
    } else {
      setGithubLogin('');
      setGithubInstallations([]);
      setGithubRepositoriesByInstallation({});
      setSelectedGithubInstallationId(null);
      setSelectedGithubRepositoryId(null);
      setGithubBranches([]);
      setGithubBranchesState('idle');
      setGithubBranchesError('');
      setSelectedGithubBranch('');
      setGithubTreeDirectories([]);
      setGithubTreeState('idle');
      setGithubTreeError('');
      setScopePaths([]);
    }

    clearGitHubCallbackParams();
  }, [githubSessionId, loadGitHubSession]);

  useEffect(() => {
    if (!githubSessionId) {
      setGithubRepositoriesByInstallation({});
      setSelectedGithubRepositoryId(null);
      setGithubBranches([]);
      setGithubBranchesState('idle');
      setGithubBranchesError('');
      setSelectedGithubBranch('');
      setGithubRepositoriesState('idle');
      setGithubTreeDirectories([]);
      setGithubTreeState('idle');
      setGithubTreeError('');
      setScopePaths([]);
      return;
    }

    if (githubSessionState === 'ready') {
      void loadGitHubRepositories(githubSessionId, githubInstallations);
    } else {
      setGithubRepositoriesByInstallation({});
      setGithubRepositoriesState(githubSessionState === 'loading' ? 'loading' : 'idle');
    }
  }, [githubSessionId, githubSessionState, githubInstallations, loadGitHubRepositories]);

  useEffect(() => {
    if (!githubSessionId || githubRepositoriesState !== 'ready') {
      return;
    }

    if (githubRepositoryChoices.length === 0) {
      setSelectedGithubRepositoryId(null);
      setSelectedGithubBranch('');
      setScopePaths([]);
      return;
    }

    const selectedChoice = githubRepositoryChoices.find((choice) => {
      return choice.installation.id === selectedGithubInstallationId &&
        choice.repository.id === selectedGithubRepositoryId;
    });

    if (selectedChoice) {
      return;
    }

    const matchingRepositoryChoice = selectedGithubRepositoryId
      ? githubRepositoryChoices.find((choice) => choice.repository.id === selectedGithubRepositoryId)
      : null;
    const nextChoice = matchingRepositoryChoice ?? (
      githubRepositoryChoices.length === 1 ? githubRepositoryChoices[0] : null
    );

    if (nextChoice) {
      setSelectedGithubInstallationId(nextChoice.installation.id);
      setSelectedGithubRepositoryId(nextChoice.repository.id);
      return;
    }

    setSelectedGithubRepositoryId(null);
    setSelectedGithubBranch('');
    setScopePaths([]);
  }, [
    githubRepositoryChoices,
    githubRepositoriesState,
    githubSessionId,
    selectedGithubInstallationId,
    selectedGithubRepositoryId
  ]);

  useEffect(() => {
    storeGitHubInstallationId(selectedGithubInstallationId);
  }, [selectedGithubInstallationId]);

  useEffect(() => {
    if (!githubSessionId || !selectedGithubInstallationId || !selectedGithubRepository) {
      setGithubBranches([]);
      setGithubBranchesState(githubSessionId && selectedGithubRepositoryId ? 'loading' : 'idle');
      setGithubBranchesError('');
      setSelectedGithubBranch('');
      setGithubTreeDirectories([]);
      setGithubTreeState('idle');
      setGithubTreeError('');
      setScopePaths([]);
      return;
    }

    void loadGitHubBranches(githubSessionId, selectedGithubInstallationId, selectedGithubRepository.id);
  }, [
    githubSessionId,
    selectedGithubInstallationId,
    selectedGithubRepository,
    selectedGithubRepository?.id,
    selectedGithubRepositoryId,
    loadGitHubBranches
  ]);

  useEffect(() => {
    if (
      codebaseSourceMode !== 'github' ||
      !hasGithubCodebaseTarget ||
      !githubSessionId ||
      !selectedGithubInstallationId ||
      !selectedGithubRepository
    ) {
      setGithubTreeDirectories([]);
      setGithubTreeState(codebaseSourceMode === 'github' && hasGithubCodebaseTarget ? 'loading' : 'idle');
      setGithubTreeError('');
      return;
    }

    const abortController = new AbortController();

    void loadGitHubRepositoryTree(
      githubSessionId,
      selectedGithubInstallationId,
      selectedGithubRepository.id,
      selectedGithubBranch,
      abortController.signal
    );

    return () => abortController.abort();
  }, [
    githubSessionId,
    codebaseSourceMode,
    hasGithubCodebaseTarget,
    selectedGithubBranch,
    selectedGithubInstallationId,
    selectedGithubRepository,
    selectedGithubRepository?.id,
    loadGitHubRepositoryTree
  ]);

  useEffect(() => {
    if (
      currentStep !== 'pricing' ||
      !hasCodebaseTarget ||
      orderedSelectedSkills.length === 0 ||
      orderedSelectedAiModels.length === 0
    ) {
      setPricingEstimate(null);
      setPricingEstimateError('');
      setPricingEstimateState('idle');
      return;
    }

    if (codebaseSourceMode === 'upload') {
      setPricingEstimate(null);
      setPricingEstimateError('');
      setPricingEstimateState('ready');
      return;
    }

    if (!selectedGithubInstallationId || !selectedGithubRepository) {
      setPricingEstimate(null);
      setPricingEstimateError('');
      setPricingEstimateState('idle');
      return;
    }

    const abortController = new AbortController();
    const agentsWithModels = getSelectedAiAgentsWithModels(orderedSelectedAiModels);
    setPricingEstimateState('loading');
    setPricingEstimateError('');

    void requestJson<PricingEstimateResponse>('/pricing', {
      method: 'POST',
      signal: abortController.signal,
      body: JSON.stringify({
        github: {
          app: {
            sessionId: githubSessionId,
            installationId: selectedGithubInstallationId,
            repositoryId: selectedGithubRepository.id,
            owner: selectedGithubRepository.owner,
            repo: selectedGithubRepository.repo,
            branch: selectedGithubBranch
          }
        },
        scopePaths,
        skills: orderedSelectedSkills,
        aiAgents: agentsWithModels
      })
    }).then((estimate) => {
      setPricingEstimate(estimate);
      setPricingEstimateState('ready');
    }).catch((error: unknown) => {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }

      setPricingEstimate(null);
      setPricingEstimateError(error instanceof Error ? error.message : String(error));
      setPricingEstimateState('error');
    });

    return () => abortController.abort();
  }, [
    currentStep,
    codebaseSourceMode,
    githubSessionId,
    hasCodebaseTarget,
    orderedSelectedAiModels,
    orderedSelectedSkills,
    scopePaths,
    selectedGithubBranch,
    selectedGithubInstallationId,
    selectedGithubRepository,
    selectedGithubRepository?.id,
    selectedGithubRepository?.owner,
    selectedGithubRepository?.repo,
  ]);

  const moveNext = () => {
    setCurrentStep(getNextWorkflowStep(currentStep));
  };

  const moveBack = () => {
    setCurrentStep(getPreviousWorkflowStep(currentStep));
  };

  const toggleSkillSelection = (skill: string) => {
    setSelectedSkills((previous) => {
      const next = new Set(previous);

      if (next.has(skill)) {
        next.delete(skill);
      } else {
        next.add(skill);
      }

      return next;
    });
    resetScanResult(true);
  };

  const selectAllSkills = () => {
    setSelectedSkills(new Set(availableSkills));
    resetScanResult(true);
  };

  const toggleModelSelection = (agent: string, model: string) => {
    const modelKey = getAiModelKey(agent, model);

    setSelectedAiModels((previous) => {
      const next = new Set(previous);

      if (next.has(modelKey)) {
        next.delete(modelKey);
      } else {
        next.add(modelKey);
      }

      return next;
    });
    resetScanResult(true);
  };

  const selectCodebaseSourceMode = (mode: CodebaseSourceMode) => {
    setCodebaseSourceMode(mode);

    if (mode === 'upload') {
      setScopePaths([]);
    }

    resetScanResult(true);
  };

  const selectUploadedCodebaseZip = (file: File | null) => {
    if (file && !file.name.toLowerCase().endsWith('.zip')) {
      setUploadedCodebaseFile(null);
      setUploadedCodebaseFileError('Select a .zip archive.');
      resetScanResult(true);
      return;
    }

    setUploadedCodebaseFile(file);
    setUploadedCodebaseFileError('');
    resetScanResult(true);
  };

  const connectGitHub = () => {
    const connectUrl = githubConfig?.connectUrl;

    if (!connectUrl) {
      setGithubSessionError('GitHub App OAuth is not configured');
      return;
    }

    const returnTo = `${window.location.pathname}${window.location.search}`;

    window.location.href = `${connectUrl}?returnTo=${encodeURIComponent(returnTo)}`;
  };

  const installGitHubApp = () => {
    const installUrl = githubConfig?.installUrl ?? selectedGithubInstallation?.htmlUrl;

    if (!installUrl) {
      setGithubSessionError('GitHub App install URL is not configured');
      return;
    }

    window.location.href = installUrl;
  };

  const refreshGitHubAccess = () => {
    void loadGitHubConfig();

    if (githubSessionId) {
      void loadGitHubSession(githubSessionId);
      void loadGitHubRepositories(githubSessionId, githubInstallations);

      if (selectedGithubInstallationId && selectedGithubRepository) {
        void loadGitHubBranches(githubSessionId, selectedGithubInstallationId, selectedGithubRepository.id);
      }
    }
  };

  const selectGithubRepository = (installationId: number, repositoryId: number) => {
    const repository = githubRepositoryChoices.find((choice) => {
      return choice.installation.id === installationId && choice.repository.id === repositoryId;
    })?.repository;

    setSelectedGithubInstallationId(installationId);
    setSelectedGithubRepositoryId(repositoryId);
    setSelectedGithubBranch(repository?.defaultBranch ?? '');
    setGithubBranches([]);
    setGithubBranchesState('loading');
    setGithubBranchesError('');
    setGithubTreeDirectories([]);
    setGithubTreeState('loading');
    setGithubTreeError('');
    setScopePaths([]);
    resetScanResult();
  };

  const selectGithubBranch = (branch: string) => {
    setSelectedGithubBranch(branch);
    setScopePaths([]);
    setGithubTreeDirectories([]);
    setGithubTreeState('loading');
    setGithubTreeError('');
    resetScanResult();
  };

  const selectRepositoryRootScope = () => {
    setScopePaths([]);
    resetScanResult();
  };

  const toggleScopePath = (scopePathToToggle: string) => {
    setScopePaths((currentScopePaths) => {
      if (currentScopePaths.includes(scopePathToToggle)) {
        return currentScopePaths.filter((path) => path !== scopePathToToggle);
      }

      const withoutCoveringAncestors = currentScopePaths.filter((path) => {
        return !scopePathToToggle.startsWith(`${path}/`);
      });

      return compactScopePaths([...withoutCoveringAncestors, scopePathToToggle]);
    });
    resetScanResult();
  };

  const changeNonStandardTokens = (value: 'yes' | 'no') => {
    setNonStandardTokens(value);
    resetScanResult();
  };

  const changeTrustedRoles = (value: string) => {
    setTrustedRoles(value);
    resetScanResult();
  };

  const changeRegularFunctions = (value: string) => {
    setRegularFunctions(value);
    resetScanResult();
  };

  const changeAdminRestrictions = (value: string) => {
    setAdminRestrictions(value);
    resetScanResult();
  };

  const changeAssumptionsFilePath = (value: string) => {
    setAssumptionsFilePath(value);
    resetScanResult();
  };

  const changeExternalContractsTrusted = (value: 'yes' | 'no') => {
    setExternalContractsTrusted(value);
    resetScanResult();
  };

  const launchScan = async (email: string) => {
    if (currentStep !== 'pricing' || !canAdvance) {
      return;
    }

    if (codebaseSourceMode === 'github' && (
      !githubSessionId ||
      !selectedGithubInstallationId ||
      !selectedGithubRepository ||
      !selectedGithubBranch
    )) {
      setLaunchError('Select a GitHub repository and branch before launching the scan.');
      setLaunchState('error');
      return;
    }

    if (codebaseSourceMode === 'upload' && !uploadedCodebaseFile) {
      setLaunchError('Select a codebase zip before launching the scan.');
      setLaunchState('error');
      return;
    }

    setLaunchState('submitting');
    setLaunchError('');
    setScanResponse(null);

    try {
      const agentsWithModels = getSelectedAiAgentsWithModels(orderedSelectedAiModels);
      const questionnaire = {
        nonStandardTokens: nonStandardTokens || undefined,
        trustedRoles: trustedRoles.trim() || undefined,
        regularFunctions: regularFunctions.trim() || undefined,
        adminRestrictions: adminRestrictions.trim() || undefined,
        assumptionsFilePath: assumptionsFilePath.trim() || undefined,
        externalContractsTrusted: externalContractsTrusted || undefined
      };
      const requestPayload = {
        scopePaths: codebaseSourceMode === 'github' ? scopePaths : [],
        skills: orderedSelectedSkills,
        aiAgents: agentsWithModels,
        pricingTier: 'free',
        resultEmail: email,
        questionnaire
      };
      const response = codebaseSourceMode === 'upload' && uploadedCodebaseFile
        ? await (() => {
          const formData = new FormData();

          formData.append('payload', JSON.stringify({
            ...requestPayload,
            codebaseUpload: {
              fileName: uploadedCodebaseFile.name
            }
          }));
          formData.append('codebaseZip', uploadedCodebaseFile, uploadedCodebaseFile.name);

          return requestJson<ScanAcceptedResponse>('/scan', {
            method: 'POST',
            body: formData
          });
        })()
        : await requestJson<ScanAcceptedResponse>('/scan', {
          method: 'POST',
          body: JSON.stringify({
            ...requestPayload,
            github: {
              app: {
                sessionId: githubSessionId,
                installationId: selectedGithubInstallationId,
                repositoryId: selectedGithubRepository?.id,
                owner: selectedGithubRepository?.owner,
                repo: selectedGithubRepository?.repo,
                branch: selectedGithubBranch
              }
            }
          })
        });

      setScanResponse(response);
      setLaunchState('accepted');
      storeScanDraft({
        ...buildScanDraft(INITIAL_STEP),
        selectedScanType: null
      });
    } catch (error) {
      setLaunchError(error instanceof Error ? error.message : String(error));
      setLaunchState('error');
    }
  };

  const openResultEmailPrompt = () => {
    if (currentStep === 'pricing' && canAdvance) {
      setIsResultEmailModalOpen(true);
    }
  };

  const submitResultEmail = (email: string) => {
    setResultEmail(email);
    setIsResultEmailModalOpen(false);
    void launchScan(email);
  };

  const overlay = (
    <>
      <SkillInfoModal
        isOpen={showSkillInfoModal}
        availableSkills={availableSkills}
        onClose={() => setShowSkillInfoModal(false)}
      />
      <ResultEmailPromptModal
        isOpen={isResultEmailModalOpen}
        initialEmail={resultEmail}
        launchState={launchState}
        onCancel={() => setIsResultEmailModalOpen(false)}
        onSubmit={submitResultEmail}
      />
    </>
  );
  const headerActions = (
    <GitHubProfileMenu
      githubSessionId={githubSessionId}
      githubLogin={githubLogin}
      githubConfigState={githubConfigState}
      githubSessionState={githubSessionState}
      githubRepositoriesState={githubRepositoriesState}
      onRefreshGitHubAccess={refreshGitHubAccess}
    />
  );
  const summary = (
    <ScanSummary
      orderedSelectedSkills={orderedSelectedSkills}
      orderedSelectedAiModels={orderedSelectedAiModels}
      target={target}
      branch={selectedGithubBranch}
      scopePaths={codebaseSourceMode === 'github' ? scopePaths : []}
      targetLabel={codebaseSourceMode === 'upload' ? 'Uploaded codebase' : 'Repository'}
      showBranch={codebaseSourceMode === 'github'}
      showScope={codebaseSourceMode === 'github'}
    />
  );
  const footer = !hasAcceptedScan ? (
    <FooterBar
      currentStep={currentStep}
      selectedSkillsCount={selectedSkills.size}
      selectedModelCount={selectedModelCount}
      selectedPricingTier={selectedPricingTier}
      selectedPricingTierAvailable={selectedPricingTierAvailable}
      orderedSelectedSkillsCount={orderedSelectedSkills.length}
      canAdvance={canAdvance}
      launchState={launchState}
      pricingFooterCopy="Free scan ready."
      launchButtonLabel="Free scan"
      onBack={moveBack}
      onBackToSelection={backToScanTypeSelection}
      onNext={moveNext}
      onLaunchScan={openResultEmailPrompt}
    />
  ) : null;

  return (
    <WorkflowShell
      currentStep={currentStep}
      stepperLabel="Scan steps"
      headerActions={headerActions}
      overlay={overlay}
      summary={summary}
      footer={footer}
    >
      {currentStep === 'agents' && (
        <AgentsStep
          loadState={loadState}
          loadError={loadError}
          availableAiAgents={availableAiAgents}
          agentModelsByAgent={agentModelsByAgent}
          selectedAiModels={selectedAiModels}
          onRetryLoadOptions={() => void loadOptions()}
          onToggleModel={toggleModelSelection}
        />
      )}

      {currentStep === 'skills' && (
        <SkillsStep
          loadState={loadState}
          loadError={loadError}
          availableSkills={availableSkills}
          selectedSkills={selectedSkills}
          onRetryLoadOptions={() => void loadOptions()}
          onSelectAllSkills={selectAllSkills}
          onShowSkillInfo={() => setShowSkillInfoModal(true)}
          onToggleSkill={toggleSkillSelection}
        />
      )}

      {currentStep === 'codebase' && (
        <CodebaseStep
          allowManualUpload
          headingDescription="Select a GitHub repository or upload a zip archive."
          codebaseSourceMode={codebaseSourceMode}
          uploadedCodebaseFileName={uploadedCodebaseFileName}
          uploadedCodebaseFileSize={uploadedCodebaseFile?.size}
          uploadedCodebaseError={uploadedCodebaseFileError}
          githubConfigState={githubConfigState}
          githubConfigError={githubConfigError}
          githubConfig={githubConfig}
          githubSessionState={githubSessionState}
          githubSessionError={githubSessionError}
          githubSessionId={githubSessionId}
          githubInstallations={githubInstallations}
          selectedGithubInstallationId={selectedGithubInstallationId}
          githubRepositoriesState={githubRepositoriesState}
          githubRepositoriesError={githubRepositoriesError}
          githubRepositoryChoices={githubRepositoryChoices}
          selectedGithubRepositoryId={selectedGithubRepositoryId}
          githubBranchesState={githubBranchesState}
          githubBranchesError={githubBranchesError}
          githubBranches={githubBranches}
          selectedGithubBranch={selectedGithubBranch}
          githubTreeState={githubTreeState}
          githubTreeError={githubTreeError}
          githubTreeDirectories={githubTreeDirectories}
          scopePaths={scopePaths}
          nonStandardTokens={nonStandardTokens}
          trustedRoles={trustedRoles}
          regularFunctions={regularFunctions}
          adminRestrictions={adminRestrictions}
          assumptionsFilePath={assumptionsFilePath}
          externalContractsTrusted={externalContractsTrusted}
          onSelectCodebaseSourceMode={selectCodebaseSourceMode}
          onSelectCodebaseZip={selectUploadedCodebaseZip}
          onConnectGitHub={connectGitHub}
          onInstallGitHubApp={installGitHubApp}
          onSelectGithubRepository={selectGithubRepository}
          onSelectGithubBranch={selectGithubBranch}
          onSelectRepositoryRootScope={selectRepositoryRootScope}
          onToggleScopePath={toggleScopePath}
          onNonStandardTokensChange={changeNonStandardTokens}
          onTrustedRolesChange={changeTrustedRoles}
          onRegularFunctionsChange={changeRegularFunctions}
          onAdminRestrictionsChange={changeAdminRestrictions}
          onAssumptionsFilePathChange={changeAssumptionsFilePath}
          onExternalContractsTrustedChange={changeExternalContractsTrusted}
        />
      )}

      {currentStep === 'pricing' && (
        <PricingStep
          hasAcceptedScan={hasAcceptedScan}
          scanResponse={scanResponse}
          pricingEstimateState={pricingEstimateState}
          pricingEstimateError={pricingEstimateError}
          pricingEstimate={pricingEstimate}
          pricingEstimateFallback="Uploaded codebases are prepared after launch. The scan is still covered by your daily free scan allowance."
          launchState={launchState}
          launchError={launchError}
          onViewRun={viewRun}
        />
      )}
    </WorkflowShell>
  );
}

export default BlockchainAuditPage;
